import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Badge, EmptyState, Select, SelectionToolbar } from '@cinesend/ui-kit'
import axios from 'axios'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import DataTable from '../data_table'
import RowMenu from '../row_menu'
import useStore from '../../hooks/use_store'
import { useNavigate, useParams } from 'react-router-dom'
import useTableFilters from '../../hooks/use_table_filters'
import { MISSING_UNIQUE_NAME } from '../../constants/category_display'
import { useConfirm } from '../../contexts/confirm_dialog'
import { useSnackbar } from '../../hooks/use_snackbar'

const formatTimestamp = (date) => (date ? dayjs(date).format('MMM D, YYYY, h:mm A') : '')
const formatDate = (date) => (date ? dayjs.utc(date).format('MMM D, YYYY') : 'N/A')

const Categories = ({ queryKey, categoryType = 'assets' }) => {
  const params = useParams()
  const { deleteModel } = useStore()
  const confirm = useConfirm()
  const { addMessage, addErrorMessage } = useSnackbar()
  const queryClient = useQueryClient()
  const [selectedKeys, setSelectedKeys] = useState([])
  const { data: airline, error: airlineError, refetch: refetchAirline } = useQuery(['airlines/', params.airlineID])
  const { data: sections } = useQuery(['categories/', `?cycleID=${params.cycleID}&type=categories`])
  const langID = airline?.languages?.[0]?.id || null

  const selectedTab = categoryType
  const isSection = selectedTab === 'categories'
  const entityNoun = isSection ? 'section' : 'category'
  const entityNounPlural = isSection ? 'sections' : 'categories'

  useEffect(() => {
    setSelectedKeys([])
  }, [selectedTab, params.cycleID])

  const sortingOptions =
    selectedTab === 'assets'
      ? [
          { label: 'Category', key: 'default_title' },
          { label: 'Slug', key: 'external_unique_name' },
          { label: 'Parent Section', key: 'section_external_unique_name' },
          { label: 'Start Date', key: 'start_date' },
          { label: 'End Date', key: 'end_date' },
        ]
      : [
          { label: 'Slug', key: 'external_unique_name' },
          { label: 'Section', key: 'default_title' },
        ]

  const sectionOptions =
    sections?.map((section) => ({
      value: section.external_unique_name,
      label: `Members of ${section.default_title || section.external_unique_name}`,
    })) ?? []

  const filteringOptions =
    selectedTab === 'assets' && sections
      ? [
          {
            label: 'Section',
            key: 'section_external_unique_name',
            type: 'checkboxes',
            options: sectionOptions.map((option) => ({ label: option.label, key: option.value })),
          },
        ]
      : null

  const { queryString, searching, sorting, filtering } = useTableFilters({
    filteringOptions,
    sortingOptions,
    enableSearch: true,
    queryExists: true,
    persistKey: `categories-table-${categoryType}-${params.cycleID}`,
  })

  const queryWithTypeParam = `${queryString}&type=${selectedTab}`

  const { data: categories, isLoading, error, refetch } = useQuery([queryKey, queryWithTypeParam])

  const navigate = useNavigate()

  const massDelete = useMutation((ids) => axios.delete('categories', { data: { ids } }), {
    onSuccess: (res) => {
      const deleted = res.data?.deleted ?? []
      const failed = res.data?.failed ?? []
      if (deleted.length) {
        addMessage(
          deleted.length === 1
            ? `${entityNoun.charAt(0).toUpperCase()}${entityNoun.slice(1)} deleted successfully.`
            : `${deleted.length} ${entityNounPlural} deleted successfully.`
        )
        queryClient.invalidateQueries([queryKey])
      }
      if (failed.length) {
        addErrorMessage(
          failed
            .map((item) => item.message)
            .filter(Boolean)
            .join(' ') || 'Some items could not be deleted.'
        )
      }
      setSelectedKeys((current) => current.filter((key) => !deleted.map(String).includes(key)))
    },
    onError: (err) => {
      addErrorMessage(err.response?.data?.message || 'An error occurred. Please contact customer support.')
    },
  })

  const requestMassDelete = async () => {
    const ids = selectedKeys.map(Number).filter((id) => !Number.isNaN(id))
    if (!ids.length || massDelete.isLoading) return
    const count = ids.length
    const noun = count === 1 ? entityNoun : entityNounPlural
    const accepted = await confirm({
      title: `Delete ${noun}?`,
      message: `Are you sure you want to delete ${count} ${noun}?`,
      confirmText: `Delete ${noun}`,
      confirmVariant: 'destructive',
    })
    if (!accepted) return
    massDelete.mutate(ids)
  }

  const typeToNameMapping = {
    categories: 'Sections',
    assets: 'Categories',
  }

  const isAssets = selectedTab === 'assets'
  const slugColumn = {
    key: 'external_unique_name',
    header: 'Slug',
    render: (data) => data.external_unique_name || (isAssets ? MISSING_UNIQUE_NAME : ''),
  }
  const titleColumn = {
    key: 'default_title',
    header: isAssets ? 'Category' : 'Section',
    render: (data) => data.default_title || '',
  }
  const itemsColumn = {
    key: 'items',
    header: 'Items',
    render: (data) => (
      <div className='flex flex-wrap gap-2'>
        {[
          ...data.category_items,
          ...data.child_categories.map((item) => item.category_values.find((v) => v.language_id === langID)),
        ].map((item, index) => (
          <Badge key={`${data.id}-${item?.title || item?.value}-${index}`}>{item?.title || item?.value}</Badge>
        ))}
      </div>
    ),
  }
  const countColumn = {
    key: 'count',
    header: isAssets ? 'Items' : 'Categories',
    render: (data) => (selectedTab === 'categories' ? data.child_categories.length : data.category_items.length),
  }
  const columns = [
    ...(isAssets
      ? [
          titleColumn,
          slugColumn,
          countColumn,
          {
            key: 'section_external_unique_name',
            header: 'Parent Section (slug)',
            render: (data) => {
              const slug = data.section_external_unique_name
              if (!slug) return ''
              const name = sections?.find((section) => section.external_unique_name === slug)?.default_title
              return name ? `${name} (${slug})` : slug
            },
          },
          { key: 'start_date', header: 'Start', render: (data) => formatDate(data.start_date) },
          { key: 'end_date', header: 'End', render: (data) => formatDate(data.end_date) },
        ]
      : [
          titleColumn,
          slugColumn,
          countColumn,
          itemsColumn,
          { key: 'created_at', header: 'Created', render: (data) => formatTimestamp(data.created_at) },
          { key: 'updated_at', header: 'Updated', render: (data) => formatTimestamp(data.updated_at) },
        ]),
    {
      key: 'actions',
      header: '',
      width: '65px',
      render: (data) => (
        <div className='flex justify-end'>
          <RowMenu
            label={`Actions for ${data.default_title}`}
            items={[
              {
                id: 'delete',
                label: 'Delete',
                tone: 'danger',
                onSelect: () =>
                  deleteModel.mutate(
                    {
                      endpoint: 'categories',
                      id: data.id,
                      deleteNoun: entityNoun,
                      deleteName: data.default_title,
                    },
                    {
                      onSuccess: () => {
                        setSelectedKeys((current) => current.filter((key) => key !== String(data.id)))
                        queryClient.invalidateQueries([queryKey])
                      },
                    }
                  ),
              },
            ]}
          />
        </div>
      ),
    },
  ]

  const selectedCount = selectedKeys.length
  const selectionLabel = `${selectedCount} ${selectedCount === 1 ? entityNoun : entityNounPlural} selected`

  return (
    <>
      <DataTable
        key={selectedTab}
        caption={typeToNameMapping[selectedTab]}
        columns={columns}
        rows={categories ?? []}
        onRowClick={(data) => navigate(`${data.id}/details`)}
        isLoading={isLoading || !airline}
        error={error || airlineError}
        onRetry={() => {
          refetch()
          refetchAirline()
        }}
        empty={
          <EmptyState
            title={`No ${typeToNameMapping[selectedTab].toLowerCase()} found!`}
            detail={`Create a new ${selectedTab === 'categories' ? 'section' : 'category'} to begin.`}
          />
        }
        searching={searching}
        sorting={sorting}
        filtering={filtering}
        filterControl={
          isAssets && sections ? (
            <Select
              label='Section'
              hideLabel
              modal={false}
              value={filtering.filters.section_external_unique_name?.[0] ?? 'all'}
              onValueChange={(value) => {
                const next = { ...filtering.filters }
                if (!value || value === 'all') delete next.section_external_unique_name
                else next.section_external_unique_name = [value]
                filtering.onFiltersChange(next)
              }}
              options={[{ value: 'all', label: 'Members of all sections' }, ...sectionOptions]}
            />
          ) : null
        }
        selectedKeys={selectedKeys}
        onSelectionChange={setSelectedKeys}
        selectAllLabel={`Select all ${entityNounPlural}`}
        selectionLabel={(data) => `Select ${data.default_title || entityNoun}`}
      />
      <SelectionToolbar
        open={selectedCount > 0}
        label={selectionLabel}
        labelText={selectionLabel}
        showDeletePrimary
        onDelete={requestMassDelete}
      />
    </>
  )
}

export default Categories
